/**
 * Pricing rules — the constraints the Customer Quotation enforces on a purchaser.
 *
 * Reads and writes `settings.get_pricing_rules` / `settings.save_pricing_rules`, both live-verified
 * (2026-08-11: `enforce: true`, four category margins populated, `default_markup_pct: 15`,
 * `base_currency: "AED"`). No mock anywhere.
 *
 * ---- Why edits are staged rather than saved per keystroke ----
 *
 * `save_pricing_rules` replaces the whole document — rules, margins and default markup together.
 * Saving on every change would mean a half-typed markup ("1" on the way to "15") briefly becoming
 * the rule the quotation screen enforces on everyone. So the card holds a draft, shows that it is
 * dirty, and writes once when asked. Discard restores the server's copy.
 *
 * ---- One thing that cannot be honoured yet ----
 *
 * `minMarkupCat` is offered but `Item.category` is unset across the live bench (defect `D2`:
 * `category_margins` is keyed Provision/Marine/Engine/Safety while the item lookup speaks a
 * different vocabulary). A category rule therefore saves correctly and matches nothing today. The
 * card says so on screen rather than letting a purchase manager believe it is in force.
 */
import { useEffect, useState } from 'react'
import { Card } from '../../components/ui'
import { usePricingRules, useSavePricingRules } from '../../lib/api/useSettings'
import { ITEM_CATEGORIES, type ItemCategory, type PricingRule, type PricingRulesSettings } from '../../lib/types'

type RuleKind = PricingRule['kind']

const KIND_LABELS: Record<RuleKind, string> = {
  minMarkup: 'Minimum markup % (all items)',
  minMarkupCat: 'Minimum markup % for a category',
  noBelowCost: 'No selling below cost',
  roundSell: 'Round selling price to nearest…',
}

/** Human sentence for a saved rule — the same phrasing the prototype's `ruleText` uses. */
export function ruleText(rule: PricingRule): string {
  switch (rule.kind) {
    case 'noBelowCost':
      return 'Never sell below cost'
    case 'minMarkup':
      return `Minimum markup ${rule.pct}% on every item`
    case 'minMarkupCat':
      return `Minimum markup ${rule.pct}% on ${rule.cat} items`
    case 'roundSell':
      return `Round selling price to the nearest ${rule.step}`
  }
}

/**
 * A client-side id for a new rule.
 *
 * Frappe assigns the real `name` on save; this only has to be unique within the unsaved draft so
 * React keys and removal work. Counter-based rather than random so it stays deterministic.
 */
let draftSeq = 0
function nextDraftId(): string {
  draftSeq += 1
  return `new-${draftSeq}`
}

function newRule(kind: RuleKind): PricingRule {
  switch (kind) {
    case 'noBelowCost':
      return { id: nextDraftId(), kind }
    case 'minMarkup':
      return { id: nextDraftId(), kind, pct: 5 }
    case 'minMarkupCat':
      return { id: nextDraftId(), kind, cat: ITEM_CATEGORIES[0], pct: 10 }
    case 'roundSell':
      return { id: nextDraftId(), kind, step: 0.25 }
  }
}

export function PricingRulesCard() {
  const { data, isLoading, error } = usePricingRules()
  const [draft, setDraft] = useState<PricingRulesSettings | null>(null)
  const [kind, setKind] = useState<RuleKind>('minMarkup')
  const [saved, setSaved] = useState(false)

  const save = useSavePricingRules({
    onSuccess: (next) => {
      setDraft(next)
      setSaved(true)
    },
  })

  // Adopt the server's copy whenever it changes, but never clobber edits in progress.
  useEffect(() => {
    if (data && !draft) setDraft(data)
  }, [data, draft])

  if (isLoading) {
    return (
      <Card style={{ marginBottom: 14 }}>
        <b>💰 Pricing rules</b>
        <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 8 }}>Loading…</div>
      </Card>
    )
  }

  if (error || !draft) {
    return (
      <Card style={{ marginBottom: 14 }}>
        <b>💰 Pricing rules</b>
        <div style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 8 }}>
          {error instanceof Error ? error.message : 'Could not load pricing rules.'}
        </div>
      </Card>
    )
  }

  const dirty = JSON.stringify(draft) !== JSON.stringify(data)

  const patch = (next: Partial<PricingRulesSettings>) => {
    setSaved(false)
    setDraft((d) => (d ? { ...d, ...next } : d))
  }

  /*
    ---- Removing a rule means ZEROING it, because there is no row to delete ----

    Probed live 2026-09-04. The rules are not a list at all: they are flat fields on the
    `Bosun Settings` Single doctype —

        no_below_cost: 1 · min_markup_pct: 5.0 · round_sell_step: 0.0 · enforce_pricing_rules: 0

    — and `save_pricing_rules` synthesises the `rules[]` array from them, omitting any whose value
    is zero. So omitting a rule from the array we SEND changes nothing (there is no row to remove),
    but sending it with a value of 0 does remove it:

        SAVE rules:[…{minMarkup, pct:0}]  ->  rules: [{noBelowCost}]      the markup rule is gone
        SAVE rules:[]                     ->  rules: [{noBelowCost}]      no change

    `noBelowCost` is the exception: it is a BOOLEAN flag with no numeric value to zero, so this API
    offers no way to clear it. That case is still detected and reported below.
  */
  const zeroed = (rule: PricingRule): PricingRule | null => {
    switch (rule.kind) {
      case 'minMarkup':
      case 'minMarkupCat':
        return { ...rule, pct: 0 }
      case 'roundSell':
        return { ...rule, step: 0 }
      case 'noBelowCost':
        // No numeric field behind it — nothing to zero. See above.
        return null
    }
  }

  /**
   * What to send: the rules still on screen, plus a zeroed copy of each one removed — which is how
   * this API expresses a deletion.
   */
  const rulesToSave = (): PricingRule[] => {
    if (!draft) return []
    const kept = draft.rules
    const removed = (data?.rules ?? [])
      .filter((r) => !kept.some((k) => k.id === r.id))
      .map(zeroed)
      .filter((r): r is PricingRule => r !== null)
    return [...kept, ...removed]
  }

  /*
    ---- What still cannot be removed, and the screen has to say so ----

    `settings.save_pricing_rules` MERGES by id and never deletes. Probed live 2026-09-04: sending
    `rules: []` returns both existing rules unchanged, and so does omitting one. Edits do persist —
    changing a markup from 5 to 7 stuck — so it is deletion specifically that is ignored.

    Without this, removing a rule looked like it worked: the row vanished from the draft, Save
    reported success, and the rule reappeared on the next read with nothing saying why.

    Detected rather than hard-coded: the moment the backend honours removals, `rules` comes back
    without the rule and this stops appearing. No code change needed. Filed as `D25`.
  */
  const removalIgnored =
    saved && data !== undefined && draft !== null && data.rules.length > draft.rules.length

  const patchRule = (id: string, next: Partial<PricingRule>) =>
    patch({
      rules: draft.rules.map((r) => (r.id === id ? ({ ...r, ...next } as PricingRule) : r)),
    })

  return (
    <Card style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <b>💰 Pricing rules</b>
          {/* What enforcement DOES is stated on the switch itself, which changes with its state —
              repeating it here left two descriptions to keep in step. */}
          <div style={{ color: 'var(--muted)', fontSize: 12.5, margin: '6px 0 10px' }}>
            Checked live on every quotation line, and again before a quotation is created.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          {saved && !dirty && (
            <span style={{ color: 'var(--ok)', fontSize: 12 }}>Saved</span>
          )}
          {dirty && (
            <button type="button" className="btn sm" onClick={() => { setDraft(data ?? null); setSaved(false) }}>
              Discard
            </button>
          )}
          <button
            type="button"
            className="btn sm pri"
            disabled={!dirty || save.isPending}
            // Removed rules travel as zeroes — see `rulesToSave`.
            onClick={() => save.mutate({ ...draft, rules: rulesToSave() })}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {removalIgnored ? (
        <div
          style={{
            fontSize: 12,
            color: 'var(--warn)',
            background: 'var(--warnbg)',
            border: '1px solid var(--warn)',
            borderRadius: 8,
            padding: '7px 10px',
            marginBottom: 10,
          }}
        >
          ⚠ <b>The server kept the rule you removed.</b> The pricing rules are flat fields on{' '}
          <code>Bosun Settings</code>, and a rule is removed by zeroing its value — which works for
          markup and rounding, but <b>“Never sell below cost”</b> is a boolean flag with no value to
          zero, so this API cannot clear it. Untick <b>Enforce these rules</b> above to stop it
          applying. Filed as <code>D25</code>.
        </div>
      ) : null}

      {save.error && (
        <div style={{ color: 'var(--danger)', fontSize: 12, marginBottom: 8 }}>
          {save.error.message}
        </div>
      )}

      {/*
        The enforcement switch governs every rule below, so it is set apart from them rather than
        sitting in the same list as the things it acts on — it was previously the first item in
        that list, hard against the first rule's title with nothing between them.

        The consequence is spelled out both ways round, because "enforce" alone does not say what
        happens when it is off, and off is the state most people will find it in.
      */}
      <label className="rulerow enforce" htmlFor="enforce-rules" style={{ cursor: 'pointer' }}>
        <input
          type="checkbox"
          id="enforce-rules"
          checked={draft.enforce}
          onChange={(e) => patch({ enforce: e.target.checked })}
        />
        <div style={{ flex: 1 }}>
          <b>Enforce these rules</b>
          <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 2 }}>
            {draft.enforce
              ? 'On — a line that breaks a rule blocks the quotation from being created or sent.'
              : 'Off — a line that breaks a rule shows a warning, and nothing is blocked.'}
          </div>
        </div>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            padding: '3px 9px',
            borderRadius: 999,
            flexShrink: 0,
            color: draft.enforce ? 'var(--ok)' : 'var(--muted)',
            border: `1px solid ${draft.enforce ? 'var(--ok)' : 'var(--line)'}`,
          }}
        >
          {draft.enforce ? 'ENFORCED' : 'WARN ONLY'}
        </span>
      </label>

      {draft.rules.length === 0 ? (
        <div
          style={{
            color: 'var(--muted)',
            fontSize: 12.5,
            padding: '14px 12px',
            border: '1px dashed var(--line)',
            borderRadius: 10,
            textAlign: 'center',
          }}
        >
          No rules yet — add one below. Enforcement with no rules blocks nothing.
        </div>
      ) : (
        draft.rules.map((rule) => (
          <div className="rulerow" key={rule.id}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <b>{ruleText(rule)}</b>
              {rule.kind === 'minMarkupCat' && (
                <div style={{ color: 'var(--warn)', fontSize: 11.5, marginTop: 2 }}>
                  ⚠ Category rules match nothing on this data — every item's category holds an
                  item-group name instead (`03.FROZEN FISH`, `01.FROZEN BEEF`…). See `D23`. Use
                  “Minimum markup % (all items)” instead.
                </div>
              )}
            </div>
            {/* The rule's own inputs, ON the row beside its name rather than wrapped underneath
                it — a markup percentage is part of the rule, not a separate paragraph. */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexShrink: 0 }}>
                {(rule.kind === 'minMarkup' || rule.kind === 'minMarkupCat') && (
                  <label className="ef" style={{ width: 92 }}>
                    <span>Markup %</span>
                    <input
                      type="number"
                      value={rule.pct}
                      onChange={(e) => patchRule(rule.id, { pct: Number(e.target.value) } as Partial<PricingRule>)}
                    />
                  </label>
                )}
                {rule.kind === 'minMarkupCat' && (
                  <label className="ef" style={{ width: 140 }}>
                    <span>Category</span>
                    <select
                      value={rule.cat}
                      onChange={(e) =>
                        patchRule(rule.id, { cat: e.target.value as ItemCategory } as Partial<PricingRule>)
                      }
                    >
                      {ITEM_CATEGORIES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </label>
                )}
                {rule.kind === 'roundSell' && (
                  <label className="ef" style={{ width: 110 }}>
                    <span>Nearest</span>
                    <input
                      type="number"
                      step="0.05"
                      value={rule.step}
                      onChange={(e) => patchRule(rule.id, { step: Number(e.target.value) } as Partial<PricingRule>)}
                    />
                  </label>
                )}
              <button
                type="button"
                className="btn sm dan"
                title={`Remove: ${ruleText(rule)}`}
                onClick={() => patch({ rules: draft.rules.filter((r) => r.id !== rule.id) })}
              >
                ✕
              </button>
            </div>
          </div>
        ))
      )}

      <div style={{ borderTop: '1px solid var(--line)', marginTop: 10, paddingTop: 12 }}>
        <b style={{ fontSize: 13 }}>Add a rule</b>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 8 }}>
          <label className="ef" style={{ flex: 1, minWidth: 240 }}>
            <span>Rule type</span>
            <select value={kind} onChange={(e) => setKind(e.target.value as RuleKind)}>
              {(Object.keys(KIND_LABELS) as RuleKind[]).map((k) => (
                <option key={k} value={k}>{KIND_LABELS[k]}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="btn pri"
            onClick={() => patch({ rules: [...draft.rules, newRule(kind)] })}
          >
            ＋ Add rule
          </button>
        </div>
      </div>
    </Card>
  )
}
